import CopyrightIcon from "@mui/icons-material/Copyright";
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow, useMediaQuery } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { motion, AnimatePresence, useMotionValue, animate } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope,
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Separator,
} from "./ui/shadcn";

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const EASE = [0.22, 1, 0.36, 1];
const TAG_SECONDS = 5.5;

// shadcn tokens for the "Aurora Drawer" concept (teal / blue)
const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": "#0b2530",
  "--card": "#ffffff",
  "--card-foreground": "#0b2530",
  "--primary": "#0d9488",
  "--primary-foreground": "#ffffff",
  "--secondary": "#e3f4f2",
  "--secondary-foreground": "#0f766e",
  "--muted": "#eef5f7",
  "--muted-foreground": "#5b7480",
  "--accent": "#e6f4f6",
  "--destructive": "#dc2626",
  "--destructive-foreground": "#ffffff",
  "--border": "#dbe8ec",
  "--input": "#d9e8ec",
  "--ring": "#14b8a6",
  "--radius": "1.75rem",
};

const TAGLINES = [
  { title: "Books that balance themselves.", sub: "Invoices, ledgers and reconciliations that stay in sync, automatically." },
  { title: "GST filing without the scramble.", sub: "Stay compliant with e-invoicing, e-way bills and ready-to-file returns." },
  { title: "Every company, one clear view.", sub: "Switch between organizations and keep multi-company ledgers organized." },
  { title: "Cash flow you can actually see.", sub: "Real-time analytics that turn raw entries into confident decisions." },
];

const FEATURES = [
  "Multi-company ledger management",
  "GST-compliant invoicing & filings",
  "Real-time cash flow analytics",
  "Role-based access & audit trail",
];

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.ld10-font, .ld10-font * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }

@keyframes ld10-blob-a { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(8vmax,6vmax,0) scale(1.12); } }
@keyframes ld10-blob-b { 0%,100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-9vmax,-7vmax,0) scale(1.1); } }
@keyframes ld10-blob-c { 0%,100% { transform: translate3d(0,0,0) scale(1); } 33% { transform: translate3d(10vmax,-4vmax,0) scale(1.15); } 66% { transform: translate3d(-6vmax,6vmax,0) scale(.9); } }
@keyframes ld10-ribbon-1 { from { transform: translate3d(-8%,0,0) rotate(-14deg); } to { transform: translate3d(8%,-6%,0) rotate(-9deg); } }
@keyframes ld10-ribbon-2 { from { transform: translate3d(6%,0,0) rotate(10deg); } to { transform: translate3d(-8%,5%,0) rotate(15deg); } }
@keyframes ld10-rays { from { background-position: 0 0; } to { background-position: 70px 0; } }
@keyframes ld10-pulse { 0% { box-shadow: 0 0 0 0 rgba(45,212,191,.65); } 70%,100% { box-shadow: 0 0 0 8px rgba(45,212,191,0); } }

.ld10-root { position: relative; isolation: isolate; display: flex; flex-direction: column; width: 100%; min-width: 0; height: 100vh; height: 100dvh; overflow-x: hidden; overflow-y: auto; background: #04141f; }

/* ---------- aurora (image-less) ---------- */
.ld10-aurora { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; contain: strict; transform: translateZ(0); background: radial-gradient(120% 90% at 0% 0%, #0b4a57 0%, #073242 38%, #04141f 100%); }
.ld10-blob { position: absolute; border-radius: 50%; width: max(520px, 62vmax); height: max(520px, 62vmax); will-change: transform; }
.ld10-blob-a { left: -14vmax; top: -18vmax; background: radial-gradient(circle, rgba(45,212,191,.6) 0%, rgba(20,184,166,.2) 40%, transparent 68%); animation: ld10-blob-a 24s ease-in-out infinite; }
.ld10-blob-b { right: -22vmax; bottom: -26vmax; background: radial-gradient(circle, rgba(37,99,235,.62) 0%, rgba(56,189,248,.2) 42%, transparent 68%); animation: ld10-blob-b 28s ease-in-out infinite; }
.ld10-blob-c { left: 24%; top: 34%; width: max(380px, 40vmax); height: max(380px, 40vmax); background: radial-gradient(circle, rgba(34,211,238,.4) 0%, transparent 66%); animation: ld10-blob-c 20s ease-in-out infinite; }
.ld10-ribbon { position: absolute; left: -20%; width: 140%; height: 22%; will-change: transform; }
.ld10-ribbon-1 { -webkit-mask-image: linear-gradient(180deg, transparent, #000 50%, transparent); mask-image: linear-gradient(180deg, transparent, #000 50%, transparent); top: 24%; background: linear-gradient(90deg, transparent 0%, rgba(94,234,212,.42) 28%, rgba(56,189,248,.46) 62%, transparent 96%); transform: rotate(-14deg); animation: ld10-ribbon-1 14s ease-in-out infinite alternate; }
.ld10-ribbon-2 { -webkit-mask-image: linear-gradient(180deg, transparent, #000 50%, transparent); mask-image: linear-gradient(180deg, transparent, #000 50%, transparent); top: 58%; background: linear-gradient(90deg, transparent 0%, rgba(59,130,246,.4) 30%, rgba(45,212,191,.38) 70%, transparent 98%); transform: rotate(10deg); animation: ld10-ribbon-2 18s ease-in-out infinite alternate; }
.ld10-rays { display: none; position: absolute; inset: 0; opacity: .9; background-image: repeating-linear-gradient(98deg, rgba(167,243,208,.07) 0, rgba(167,243,208,.07) 1px, transparent 1px, transparent 70px); -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 30%, transparent 92%); mask-image: linear-gradient(180deg, transparent 0%, #000 30%, transparent 92%); }
.ld10-vignette { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(4,20,31,0) 45%, rgba(4,20,31,.6) 100%); }
.ld10-still .ld10-blob, .ld10-still .ld10-ribbon, .ld10-still .ld10-rays { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .ld10-blob, .ld10-ribbon, .ld10-rays, .ld10-pulse { animation: none !important; } }

/* ---------- hero (left) ---------- */
.ld10-hero { position: relative; z-index: 1; order: -1; flex: 0 0 auto; min-width: 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 20px; padding: 26px 24px 40px; color: #fff; }
.ld10-kicker, .ld10-chips { display: none; }
.ld10-hero-mid { display: flex; flex-direction: column; align-items: center; gap: 16px; min-width: 0; max-width: 100%; }
.ld10-logo { display: block; height: clamp(34px, 11vw, 48px); width: auto; max-width: 100%; filter: brightness(0) invert(1) drop-shadow(0 6px 24px rgba(45,212,191,.35)); }
.ld10-rotator { min-width: 0; max-width: 100%; }
.ld10-tag-wrap { min-height: 3.7rem; }
.ld10-tagline { margin: 0; font-size: clamp(1.2rem, 5.6vw, 1.6rem); overflow-wrap: break-word; line-height: 1.14; font-weight: 700; letter-spacing: -.02em; color: #fff; max-width: 15em; }
.ld10-word { display: inline-block; }
.ld10-accent { background: linear-gradient(100deg, #99f6e4 0%, #7dd3fc 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent; }
.ld10-tagsub { display: none; margin: 16px 0 0; max-width: 30em; font-size: .98rem; line-height: 1.65; color: rgba(224,255,251,.74); }
.ld10-dots { display: flex; align-items: center; justify-content: center; margin-top: -4px; }
.ld10-dot { appearance: none; border: 0; padding: 0; background: transparent; cursor: pointer; display: flex; align-items: center; justify-content: center; width: 36px; height: 44px; border-radius: 12px; transition: width .4s cubic-bezier(.22,1,.36,1); }
.ld10-dot[aria-current="true"] { width: 64px; }
.ld10-dot:focus-visible { outline: 2px solid #99f6e4; outline-offset: 2px; }
.ld10-dot-track { position: relative; display: block; width: 8px; height: 6px; border-radius: 3px; overflow: hidden; background: rgba(255,255,255,.3); transition: width .4s cubic-bezier(.22,1,.36,1), background-color .2s ease; }
.ld10-dot:hover .ld10-dot-track { background: rgba(255,255,255,.5); }
.ld10-dot[aria-current="true"] .ld10-dot-track { width: 44px; background: rgba(255,255,255,.22); }
.ld10-prog { position: absolute; inset: 0; border-radius: 3px; background: #fff; transform-origin: left center; }
.ld10-hero .ui-badge-outline { color: #e6fffb; border-color: rgba(255,255,255,.2); background: rgba(255,255,255,.08); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
.ld10-kicker-badge { gap: 10px; padding: .4rem 1rem !important; font-size: .7rem !important; letter-spacing: .16em; text-transform: uppercase; }
.ld10-pulse { width: 8px; height: 8px; border-radius: 50%; background: #2dd4bf; animation: ld10-pulse 2.2s ease-out infinite; }
.ld10-chip { gap: 8px; padding: .5rem 1rem !important; font-size: .8rem !important; font-weight: 500 !important; transition: background-color .2s ease, border-color .2s ease; }
.ld10-chip:hover { background: rgba(255,255,255,.16) !important; border-color: rgba(255,255,255,.34) !important; }
.ld10-chip svg { font-size: 14px; color: #5eead4; }

/* ---------- drawer panel (right) ---------- */
.ld10-panel { position: relative; z-index: 2; flex: 1 0 auto; display: flex; flex-direction: column; align-items: center; width: 100%; padding: 36px clamp(18px, 6vw, 32px) 20px; background: rgba(255,255,255,.97); -webkit-backdrop-filter: blur(24px); backdrop-filter: blur(24px); border-radius: 28px 28px 0 0; box-shadow: 0 -16px 48px rgba(2,16,26,.35); }
.ld10-grip { position: absolute; top: 10px; left: 50%; width: 44px; height: 4px; margin-left: -22px; border-radius: 2px; background: #cbdde2; }
.ld10-panel-inner { width: 100%; min-width: 0; max-width: 380px; margin-block: auto; }
.ld10-copy { flex-shrink: 0; margin: 28px 0 0; text-align: center; font-size: .73rem; color: #8aa1aa; }

.ld10-root .ld10-formcard { background: transparent; border: 0; box-shadow: none; border-radius: 0; }
.ld10-root .ld10-formcard .ui-card-header { padding: 0 0 22px; gap: 14px; align-items: flex-start; }
.ld10-root .ld10-formcard .ui-card-content { padding: 0; }
.ld10-root .ld10-formcard .ui-card-footer { padding: 0; margin-top: 22px; flex-direction: column; align-items: stretch; gap: 18px; }
.ld10-root .ld10-title { font-size: 1.9rem; font-weight: 800; letter-spacing: -.03em; line-height: 1.1; margin-bottom: 8px; }
.ld10-root .ld10-formcard .ui-card-description { font-size: .92rem; line-height: 1.5; }
.ld10-secure { gap: 6px; padding: .3rem .8rem !important; font-size: .68rem !important; letter-spacing: .08em; text-transform: uppercase; }

/* floating-label pill fields (built on the shadcn Input + Label) */
.ld10-field { position: relative; }
.ld10-root { color-scheme: light; }
.ld10-root .ld10-input { height: 58px; border-radius: 999px; padding: 22px 20px 6px 22px; font-size: .95rem; color: var(--foreground); -webkit-text-fill-color: var(--foreground); caret-color: var(--foreground); background: #e6f0f3; border: 1.5px solid #8fb0ba; transition: background-color .25s ease, border-color .25s ease, box-shadow .25s ease; }
.ld10-root .ld10-input.pr-caps { padding-right: 52px; }
.ld10-root .ld10-input.pr-eye { padding-right: 96px; }
.ld10-root .ld10-input:hover { background: #dcebef; border-color: #4f9ea0; }
.ld10-root .ld10-input:focus, .ld10-root .ld10-input:focus-visible { background: #fff; border-color: #0d9488; box-shadow: 0 0 0 4px rgba(20,184,166,.22); }
.ld10-root .ld10-input:-webkit-autofill, .ld10-root .ld10-input:-webkit-autofill:hover, .ld10-root .ld10-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 1000px #e6f0f3 inset; -webkit-text-fill-color: var(--foreground); caret-color: var(--foreground); }
.ld10-root .ld10-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 1000px #fff inset; }
.ld10-root .ld10-label { position: absolute; z-index: 1; left: 23px; top: 20px; max-width: calc(100% - 60px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .95rem; font-weight: 400; line-height: 1.2; color: #4d6772; pointer-events: none; transform-origin: left top; transition: transform .22s cubic-bezier(.22,1,.36,1), color .2s ease; }
.ld10-root .ld10-input:focus ~ .ld10-label, .ld10-root .ld10-input:not(:placeholder-shown) ~ .ld10-label, .ld10-root .ld10-input:-webkit-autofill ~ .ld10-label { transform: translateY(-11px) scale(.78); }
.ld10-root .ld10-input:focus ~ .ld10-label { color: #0f766e; font-weight: 600; }
.ld10-adorn { position: absolute; z-index: 2; top: 0; right: 8px; height: 58px; display: flex; align-items: center; gap: 4px; }
.ld10-caps-icon { color: #f59e0b; font-size: 20px; }
.ld10-root .ld10-eye { width: 44px; height: 44px; min-width: 44px; padding: 0; border-radius: 50%; color: #5b7480; }
.ld10-hint { display: flex; align-items: center; min-height: 20px; margin: 4px 0 6px 22px; font-size: .75rem; color: #b45309; }
.ld10-hint span { display: inline-flex; align-items: center; gap: 5px; }
.ld10-row { display: flex; justify-content: flex-end; margin: 0 0 8px; }
.ld10-link { color: #0f766e; font-weight: 700; font-size: .84rem; text-decoration: none; border-radius: 6px; }
.ld10-row .ld10-link { display: inline-flex; align-items: center; min-height: 44px; padding: 0 8px; }
.ld10-link:hover { text-decoration: underline; text-underline-offset: 3px; }
.ld10-link:focus-visible { outline: 2px solid var(--ring); outline-offset: 3px; }
.ld10-signup { margin: 0; text-align: center; overflow-wrap: anywhere; font-size: .875rem; color: var(--muted-foreground); }
.ld10-signup .ld10-link { display: inline-block; padding: 13px 4px; margin: -13px -4px; font-size: .875rem; color: #0d9488; }

.ld10-alert-wrap { overflow: hidden; padding-bottom: 16px; }
.ld10-root .ld10-alert { display: flex; align-items: flex-start; gap: 10px; border-radius: 20px; padding: 12px 16px; background: #fef2f2; border-color: #fecaca; color: #b91c1c; font-size: .84rem; line-height: 1.45; overflow-wrap: anywhere; min-width: 0; }
.ld10-alert span { min-width: 0; }
.ld10-actions { display: flex; flex-direction: column; gap: 12px; }
.ld10-root .ld10-submit { height: 54px; width: 100%; border-radius: 999px; font-size: 1rem; font-weight: 700; color: #fff; border: 0; background: linear-gradient(100deg, #0d9488 0%, #0891b2 50%, #2563eb 100%); background-size: 200% 100%; background-position: 0% 50%; box-shadow: 0 10px 26px rgba(13,148,136,.32); transition: background-position .5s ease, box-shadow .25s ease, opacity .15s ease; }
.ld10-root .ld10-submit:hover { opacity: 1; background-position: 100% 50%; box-shadow: 0 14px 32px rgba(13,148,136,.42); }
.ld10-root .ld10-submit:disabled { opacity: .7; }
.ld10-root .ld10-pill-outline { height: 50px; width: 100%; border-radius: 999px; border: 1.5px solid #bfe3df; color: #0f766e; font-weight: 600; background: #fff; transition: background-color .2s ease, border-color .2s ease; }
.ld10-root .ld10-pill-outline:hover { background: #effaf8; border-color: #14b8a6; }
.ld10-root .ui-btn:active { transform: none; }

/* company selection cards */
.ld10-companies { display: flex; flex-direction: column; gap: 10px; max-height: min(46vh, 380px); overflow-y: auto; padding: 4px; margin: -4px -4px 14px; }
.ld10-root .ld10-company { display: flex; align-items: center; gap: 14px; min-width: 0; min-height: 56px; padding: 14px 16px; border: 1.5px solid var(--border); border-radius: 22px; background: #f4f9fa; box-shadow: none; cursor: pointer; outline: none; transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease; }
.ld10-root .ld10-company:hover { border-color: #8fd0ca; }
.ld10-root .ld10-company:focus-visible { box-shadow: 0 0 0 4px rgba(20,184,166,.3); }
.ld10-root .ld10-company.is-selected { border-color: var(--ring); background: #fff; box-shadow: 0 8px 24px rgba(13,148,136,.16), 0 0 0 4px rgba(20,184,166,.12); }
.ld10-avatar { flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; font-size: .95rem; color: #fff; background: linear-gradient(135deg, #0d9488, #2563eb); }
.ld10-company-name { flex: 1; min-width: 0; font-weight: 700; font-size: .92rem; color: var(--foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ld10-tick { flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; background: var(--primary); color: #fff; }
.ld10-ring { flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%; border: 1.5px solid #c3d6dc; }

/* session dialog */
.ld10-dialog { background: #fff; }
.ld10-dlg-banner { padding: 24px 24px 20px; color: #fff; background: linear-gradient(120deg, #073242 0%, #0d9488 58%, #2563eb 100%); }
.ld10-dlg-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.ld10-dlg-icon { flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%; display: grid; place-items: center; background: rgba(255,255,255,.18); }
.ld10-dlg-title { margin: 0; font-size: 1.05rem; font-weight: 800; line-height: 1.3; }
.ld10-dlg-sub { margin: 2px 0 0; font-size: .78rem; opacity: .8; }
.ld10-dlg-note { margin: 0; padding: 10px 14px; border-radius: 16px; background: rgba(255,255,255,.14); font-size: .8rem; line-height: 1.5; }
.ld10-dlg-body { padding: 18px 20px 6px; }
.ld10-dlg-label { margin: 0 0 12px; font-size: .72rem; font-weight: 700; letter-spacing: .8px; text-transform: uppercase; color: #7d97a1; }
.ld10-root .ld10-session, .ld10-dialog .ld10-session { display: flex; align-items: center; gap: 12px; padding: 12px 14px; margin-bottom: 10px; border-radius: 20px; background: #f4f9fa; box-shadow: none; }
.ld10-session-icon { flex-shrink: 0; width: 38px; height: 38px; border-radius: 12px; display: grid; place-items: center; background: #d5f2ee; }
.ld10-session-info { flex: 1; min-width: 0; }
.ld10-session-name { font-weight: 700; font-size: .87rem; color: #0b2530; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ld10-session-meta { display: flex; align-items: center; gap: 5px; margin-top: 3px; font-size: .73rem; color: #7d97a1; }
.ld10-dlg-foot { padding: 6px 20px 22px; }
.ld10-dialog .ld10-pill { border-radius: 999px; }
.ld10-dialog .ld10-signout { min-width: 88px; height: 44px; font-weight: 700; font-size: .78rem; }
.ld10-dialog .ld10-cancel { width: 100%; height: 48px; border-radius: 999px; color: #5b7480; font-weight: 600; border: 1.5px solid #dbe8ec; background: #fff; }
.ld10-dialog .ld10-cancel:hover { background: #f1f7f8; }

/* ---------- breakpoints ---------- */
.ld10-formcard, .ld10-formcard .ui-card-title, .ld10-formcard .ui-card-description { overflow-wrap: anywhere; min-width: 0; }

/* tablet portrait (600-899): hero grows a little, form becomes a centred sheet */
@media (min-width: 600px) and (max-width: 899px) {
  .ld10-hero { padding: 36px 40px 56px; gap: 22px; }
  .ld10-hero-mid { gap: 20px; }
  .ld10-logo { height: 56px; }
  .ld10-tag-wrap { min-height: 5.6rem; }
  .ld10-tagline { font-size: 2.1rem; max-width: 17em; }
  .ld10-tagsub { display: block; margin-top: 12px; font-size: .95rem; }
  .ld10-panel { width: min(560px, 100%); align-self: center; flex: 1 0 auto; padding: 40px 40px 22px; }
  .ld10-panel-inner { max-width: 420px; }
}

/* desktop: side-by-side, form drawer on the right */
@media (min-width: 900px) {
  .ld10-root { flex-direction: row; overflow: hidden; }
  .ld10-hero { flex: 1 1 0%; align-items: flex-start; text-align: left; justify-content: space-between; gap: 32px; padding: clamp(32px, 4vw, 64px) clamp(36px, 5vw, 88px); }
  .ld10-kicker { display: inline-flex; }
  .ld10-hero-mid { align-items: flex-start; gap: 28px; }
  .ld10-logo { height: clamp(56px, 4vw + 20px, 120px); }
  .ld10-tag-wrap { min-height: clamp(8.5rem, 4vw + 6rem, 13rem); }
  .ld10-tagline { font-size: clamp(2rem, 1.2rem + 1.7vw, 3.8rem); max-width: 14em; }
  .ld10-tagsub { display: block; font-size: clamp(.92rem, .8rem + .25vw, 1.1rem); }
  .ld10-dots { justify-content: flex-start; margin-top: 14px; margin-left: -8px; }
  .ld10-chips { display: flex; flex-wrap: wrap; gap: 10px; max-width: 640px; margin: 0; padding: 0; list-style: none; }
  .ld10-chips li { min-width: 0; max-width: 100%; }
  .ld10-panel { order: 0; flex: 0 0 clamp(400px, 32vw, 540px); width: clamp(400px, 32vw, 540px); align-self: stretch; height: 100%; overflow-y: auto; padding: clamp(28px, 5vh, 48px) clamp(28px, 3vw, 48px) 22px; border-radius: 36px 0 0 36px; box-shadow: -30px 0 90px rgba(2,16,26,.45); }
  .ld10-panel-inner { max-width: 400px; }
  .ld10-grip { top: 50%; left: 12px; width: 4px; height: 56px; margin: -28px 0 0; }
}

/* short desktop / landscape tablet */
@media (min-width: 900px) and (max-height: 700px) {
  .ld10-logo { height: 52px; }
  .ld10-tagsub, .ld10-chips { display: none; }
  .ld10-tag-wrap { min-height: 7rem; }
}

/* short portrait phones */
@media (max-width: 899px) and (max-height: 640px) {
  .ld10-hero { gap: 8px; padding: 14px 20px 22px; }
  .ld10-hero-mid { gap: 6px; }
  .ld10-logo { height: 32px; }
  .ld10-tag-wrap { min-height: 2.9rem; }
  .ld10-tagline { font-size: 1.2rem; }
  .ld10-tagsub { display: none; }
  .ld10-dots { margin-top: -8px; }
  .ld10-panel { padding-top: 28px; }
}

/* very short viewports (landscape phones): compact one-line brand strip, tighter form */
@media (max-height: 520px) {
  .ld10-secure-wrap { display: none; }
  .ld10-root .ld10-formcard .ui-card-header { padding-bottom: 12px; }
  .ld10-root .ld10-title { font-size: 1.5rem; margin-bottom: 4px; }
  .ld10-companies { max-height: none; }
  .ld10-copy { margin-top: 14px; }
  .ld10-panel { padding-top: 26px; padding-bottom: 14px; }
}
@media (max-height: 430px) {
  .ld10-root .ld10-formcard .ui-card-description { display: none; }
  .ld10-root .ld10-formcard .ui-card-footer { margin-top: 12px; gap: 8px; }
}
@media (max-width: 899px) and (max-height: 520px) {
  .ld10-hero { flex-direction: row; justify-content: center; align-items: center; gap: 16px; padding: 8px 16px 18px; text-align: left; }
  .ld10-hero-mid { flex-direction: row; align-items: center; gap: 16px; }
  .ld10-logo { height: 28px; flex-shrink: 0; }
  .ld10-tag-wrap { min-height: 0; }
  .ld10-tagline { font-size: 1rem; max-width: none; }
  .ld10-dots { display: none; }
  .ld10-panel { padding-top: 24px; }
}

/* small phones */
@media (max-width: 380px) {
  .ld10-root .ld10-title { font-size: 1.65rem; }
  .ld10-root .ld10-input { padding-right: 16px; }
  .ld10-root .ld10-input.pr-caps { padding-right: 48px; }
  .ld10-root .ld10-input.pr-eye { padding-right: 92px; }
}
@media (max-width: 480px) {
  .ld10-dialog .ld10-session { flex-wrap: wrap; }
  .ld10-dialog .ld10-session-info { flex: 1 1 calc(100% - 60px); }
  .ld10-dialog .ld10-signout { width: 100%; }
  .ld10-dlg-banner { padding: 20px 18px 16px; }
  .ld10-dlg-body { padding-left: 16px; padding-right: 16px; }
  .ld10-dlg-foot { padding-left: 16px; padding-right: 16px; }
}
`;

// Rotating tagline: plain timers + CSS transitions (no JS animation engine), so it advances on its own.
function TaglineRotator() {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(false);

  // Fade/slide the new tagline in shortly after each change.
  useEffect(() => {
    setShown(false);
    const t = setTimeout(() => setShown(true), 40);
    return () => clearTimeout(t);
  }, [index]);

  // Auto-advance. Any change of index (auto or click) restarts the timer.
  useEffect(() => {
    const t = setTimeout(() => setIndex((i) => (i + 1) % TAGLINES.length), TAG_SECONDS * 1000);
    return () => clearTimeout(t);
  }, [index]);

  const item = TAGLINES[index];
  const words = item.title.split(" ");
  const enter = (delay) => ({
    opacity: shown ? 1 : 0.001,
    transform: shown ? "translateY(0)" : "translateY(14px)",
    transition: shown ? "opacity .55s ease " + delay + "s, transform .55s cubic-bezier(.22,1,.36,1) " + delay + "s" : "none",
  });

  return (
    <div className="ld10-rotator">
      <div className="ld10-tag-wrap" aria-live="off">
        <p className="ld10-tagline">
          {words.map((w, i) => (
            <Fragment key={index + "-" + i}>
              {i > 0 ? " " : null}
              <span className={"ld10-word" + (i === words.length - 1 ? " ld10-accent" : "")} style={enter(i * 0.05)}>
                {w}
              </span>
            </Fragment>
          ))}
        </p>
        <p className="ld10-tagsub" style={enter(0.2)}>{item.sub}</p>
      </div>

      <div className="ld10-dots" role="group" aria-label="Product highlights">
        {TAGLINES.map((t, i) => {
          const active = i === index;
          return (
            <button
              key={t.title}
              type="button"
              className="ld10-dot"
              onClick={() => setIndex(i)}
              aria-current={active ? "true" : undefined}
              aria-label={"Show highlight " + (i + 1) + " of " + TAGLINES.length + ": " + t.title}
            >
              <span className="ld10-dot-track">
                {active && (
                  <span
                    key={index}
                    className="ld10-prog"
                    style={{
                      transform: shown ? "scaleX(1)" : "scaleX(0)",
                      transition: shown ? "transform " + TAG_SECONDS + "s linear" : "none",
                    }}
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LoginDesign10({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Motion preferences + layout
  const reduce = !!useReducedMotion();
  const isDesktop = useMediaQuery("(min-width:900px)", { noSsr: true });

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#0d9488" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#0d9488" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#0d9488" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#0d9488" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ---------- motion presets (all collapse to instant when the user prefers reduced motion) ----------
  const viewV = {
    // enter is transform-only (never opacity 0) so the form/inputs can't get stuck invisible; exit fades out
    hidden: { x: reduce ? 0 : 24 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: reduce ? 0 : 0.35, ease: EASE, staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.05 },
    },
    exit: { opacity: 0, x: reduce ? 0 : -24, transition: { duration: reduce ? 0 : 0.2 } },
  };
  const itemV = {
    hidden: { opacity: 1, y: reduce ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.5, ease: EASE } },
  };
  // Input groups never use opacity 0: they stay visible even if a parent variant never resolves.
  const fieldV = {
    hidden: { y: reduce ? 0 : 16 },
    show: { y: 0, transition: { duration: reduce ? 0 : 0.5, ease: EASE } },
  };
  const hoverBtn = reduce ? undefined : { y: -2, scale: 1.015 };
  const tapBtn = { scale: 0.97 };
  const dur = (d, delay = 0) => (reduce ? { duration: 0 } : { duration: d, delay, ease: EASE });

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key={errorMessage}
          className="ld10-alert-wrap"
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto", x: reduce ? 0 : [0, -8, 8, -5, 5, 0] }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ opacity: { duration: reduce ? 0 : 0.2 }, height: { duration: reduce ? 0 : 0.25 }, x: { duration: reduce ? 0 : 0.45, ease: "easeOut" } }}
        >
          <Alert variant="destructive" className="ld10-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0, mt: "1px" }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const capsHint = (live) => (
    <div className="ld10-hint" {...(live ? { role: "status", "aria-live": "polite" } : {})}>
      {capsLockOn && (
        <motion.span initial={{ opacity: 1, y: reduce ? 0 : -4 }} animate={{ opacity: 1, y: 0 }} transition={dur(0.2)}>
          <WarningAmberIcon sx={{ fontSize: 14 }} />
          Caps Lock is ON
        </motion.span>
      )}
    </div>
  );

  return (
    <ThemeScope vars={THEME_VARS} className={"ld10-root ld10-font" + (reduce ? " ld10-still" : "")}>
      <style>{STYLES}</style>

      {/* ── AURORA BACKDROP (no imagery) ── */}
      <div className="ld10-aurora" aria-hidden="true">
        <div className="ld10-blob ld10-blob-a" />
        <div className="ld10-blob ld10-blob-b" />
        <div className="ld10-blob ld10-blob-c" />
        <div className="ld10-ribbon ld10-ribbon-1" />
        <div className="ld10-ribbon ld10-ribbon-2" />
        <div className="ld10-rays" />
        <div className="ld10-vignette" />
      </div>

      {/* ── DRAWER PANEL (right) ── */}
      <motion.main
        className="ld10-panel"
        initial={reduce ? false : isDesktop ? { x: 64 } : { y: 40 }}
        animate={{ x: 0, y: 0 }}
        transition={{ duration: reduce ? 0 : 0.8, delay: reduce ? 0 : 0.1, ease: EASE }}
      >
        <span className="ld10-grip" aria-hidden="true" />

        <div className="ld10-panel-inner">
          <AnimatePresence mode="wait" initial>
            {!isCompanySelectionVisible ? (

              /* ── LOGIN FORM ── */
              <motion.div key="login" variants={viewV} initial="hidden" animate="show" exit="exit">
                <Card className="ld10-formcard">
                  <CardHeader>
                    <motion.div variants={itemV} className="ld10-secure-wrap">
                      <Badge variant="secondary" className="ld10-secure">
                        <LockOutlinedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                        Secure sign-in
                      </Badge>
                    </motion.div>
                    <motion.div variants={itemV}>
                      <CardTitle className="ld10-title" role="heading" aria-level={1}>Welcome</CardTitle>
                      <CardDescription>Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div variants={fieldV}>
                        <div className="ld10-field">
                          <Input
                            id="ld10-identifier"
                            name="identifier"
                            type="text"
                            autoComplete="username"
                            placeholder=" "
                            className={"ld10-input" + (capsLockOn ? " pr-caps" : "")}
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck}
                            onFocus={handleCapsLockCheck}
                            onBlur={() => setCapsLockOn(false)}
                          />
                          <Label htmlFor="ld10-identifier" className="ld10-label">Username, Email or Mobile</Label>
                          {capsLockOn && (
                            <div className="ld10-adorn">
                              <WarningAmberIcon className="ld10-caps-icon" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        {capsHint(false)}
                      </motion.div>

                      <motion.div variants={fieldV}>
                        <div className="ld10-field">
                          <Input
                            id="ld10-password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder=" "
                            className="ld10-input pr-eye"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck}
                            onFocus={handleCapsLockCheck}
                            onBlur={() => setCapsLockOn(false)}
                          />
                          <Label htmlFor="ld10-password" className="ld10-label">Password</Label>
                          <div className="ld10-adorn">
                            {capsLockOn && <WarningAmberIcon className="ld10-caps-icon" aria-hidden="true" />}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ld10-eye"
                              onClick={handleTogglePasswordVisibility}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              aria-pressed={showPassword}
                            >
                              {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                            </Button>
                          </div>
                        </div>
                        {capsHint(true)}
                      </motion.div>

                      <motion.div variants={itemV} className="ld10-row">
                        <Link to="/forgot-password" className="ld10-link">Forgot password?</Link>
                      </motion.div>

                      {errorAlert}

                      <motion.div variants={itemV} className="ld10-actions">
                        <MotionButton
                          type="submit"
                          className="ld10-submit"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          whileHover={isLoading ? undefined : hoverBtn}
                          whileTap={isLoading ? undefined : tapBtn}
                        >
                          {isLoading ? (
                            <>
                              <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                              Signing in...
                            </>
                          ) : (
                            <>
                              Sign In
                              <ArrowForwardIcon sx={{ fontSize: 20 }} aria-hidden="true" />
                            </>
                          )}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <MotionButton
                            type="button"
                            variant="outline"
                            className="ld10-pill-outline"
                            whileHover={hoverBtn}
                            whileTap={tapBtn}
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}
                          >
                            Login with Biometric
                          </MotionButton>
                        )}
                      </motion.div>
                    </form>
                  </CardContent>

                  <CardFooter>
                    <motion.div variants={itemV}><Separator /></motion.div>
                    <motion.p variants={itemV} className="ld10-signup">
                      Don't have an account?{" "}
                      <Link to="/register" className="ld10-link">Sign up now</Link>
                    </motion.p>
                  </CardFooter>
                </Card>
              </motion.div>

            ) : (

              /* ── COMPANY SELECTION ── */
              <motion.div key="company" variants={viewV} initial="hidden" animate="show" exit="exit">
                <Card className="ld10-formcard">
                  <CardHeader>
                    <motion.div variants={itemV} className="ld10-secure-wrap">
                      <Badge variant="secondary" className="ld10-secure">
                        <CheckIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                        Signed in
                      </Badge>
                    </motion.div>
                    <motion.div variants={itemV}>
                      <CardTitle className="ld10-title" role="heading" aria-level={1}>Select Organization</CardTitle>
                      <CardDescription>Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <motion.div variants={itemV} className="ld10-companies" role="radiogroup" aria-label="Choose your organization">
                      {companies.map((c, i) => {
                        const selected = selectedCompany?.companyId === c.companyId;
                        return (
                          <MotionCard
                            key={c.companyId}
                            role="radio"
                            aria-checked={selected}
                            tabIndex={0}
                            className={"ld10-company" + (selected ? " is-selected" : "")}
                            initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={dur(0.4, 0.12 + i * 0.06)}
                            whileHover={reduce ? undefined : { y: -2 }}
                            whileTap={{ scale: 0.985 }}
                            onClick={() => setSelectedCompany(c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedCompany(c);
                              }
                            }}
                          >
                            <span className="ld10-avatar" aria-hidden="true">
                              {(c.companyName || "?").trim().charAt(0).toUpperCase()}
                            </span>
                            <span className="ld10-company-name" title={c.companyName}>{c.companyName}</span>
                            <AnimatePresence initial={false} mode="wait">
                              {selected ? (
                                <motion.span
                                  key="tick"
                                  className="ld10-tick"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 22 }}
                                >
                                  <CheckIcon sx={{ fontSize: 15 }} aria-hidden="true" />
                                </motion.span>
                              ) : (
                                <span key="ring" className="ld10-ring" aria-hidden="true" />
                              )}
                            </AnimatePresence>
                          </MotionCard>
                        );
                      })}
                    </motion.div>

                    {errorAlert}

                    <motion.div variants={itemV}>
                      <MotionButton
                        type="button"
                        className="ld10-submit"
                        whileHover={hoverBtn}
                        whileTap={tapBtn}
                        onClick={() => FN_SelectCompany()}
                      >
                        Continue to Dashboard
                        <ArrowForwardIcon sx={{ fontSize: 20 }} aria-hidden="true" />
                      </MotionButton>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="ld10-copy">
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </p>
      </motion.main>

      {/* ── HERO: large logo + rotating tagline ── */}
      <section className="ld10-hero" aria-label="About ReonBook">
        <motion.div
          className="ld10-kicker"
          initial={{ opacity: 1, y: reduce ? 0 : -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={dur(0.6, 0.1)}
        >
          <Badge variant="outline" className="ld10-kicker-badge">
            <span className="ld10-pulse" aria-hidden="true" />
            Enterprise Accounting Platform
          </Badge>
        </motion.div>

        <div className="ld10-hero-mid">
          <motion.img
            className="ld10-logo"
            src="/RBLOGOBLUE.svg"
            alt="ReonBook"
            initial={{ opacity: 1, scale: reduce ? 1 : 0.92 }}
            animate={reduce ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1, y: [0, -6, 0] }}
            transition={{
              opacity: { duration: reduce ? 0 : 0.8 },
              scale: { duration: reduce ? 0 : 0.9, ease: EASE },
              y: { duration: 0.01, repeat: 0, ease: "easeInOut", delay: 1 },
            }}
          />
          <TaglineRotator />
        </div>

        <ul className="ld10-chips" aria-label="Key features">
          {FEATURES.map((feat, i) => (
            <motion.li
              key={feat}
              initial={{ opacity: 1, y: reduce ? 0 : 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={dur(0.5, 0.5 + i * 0.08)}
              whileHover={reduce ? undefined : { y: -2 }}
            >
              <Badge variant="outline" className="ld10-chip">
                <CheckIcon aria-hidden="true" />
                {feat}
              </Badge>
            </motion.li>
          ))}
        </ul>
      </section>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld10-font"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{ enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(4,20,31,0.55)" } }}
        PaperProps={{ sx: { borderRadius: "28px", overflow: "hidden", backgroundImage: "none", m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" }, maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" } } }}
        aria-labelledby="ld10-session-title"
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="ld10-dialog ld10-font">
            {/* Top banner */}
            <div className="ld10-dlg-banner">
              <div className="ld10-dlg-head">
                <div className="ld10-dlg-icon">
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </div>
                <div>
                  <h2 id="ld10-session-title" className="ld10-dlg-title">Session Limit Reached</h2>
                  <p className="ld10-dlg-sub">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </p>
                </div>
              </div>
              <p className="ld10-dlg-note">
                Sign out of one of the devices below to log in on this device.
              </p>
            </div>

            {/* Device list */}
            <div className="ld10-dlg-body">
              <p className="ld10-dlg-label">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <MotionCard
                  key={session.id}
                  className="ld10-session"
                  initial={{ opacity: 1, y: reduce ? 0 : 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={dur(0.45, 0.1 + idx * 0.07)}
                  whileHover={reduce ? undefined : { y: -1 }}
                >
                  {/* Icon */}
                  <div className="ld10-session-icon">{deviceIcon(session.device_type)}</div>

                  {/* Info */}
                  <div className="ld10-session-info">
                    <div className="ld10-session-name">{session.device_name || "Unknown Device"}</div>
                    <div className="ld10-session-meta">
                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                      <span>{timeAgo(session.last_activity)}</span>
                      {session.ip_address && <span>· {session.ip_address}</span>}
                    </div>
                  </div>

                  {/* Action */}
                  <MotionButton
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="ld10-pill ld10-signout"
                    disabled={kickingSessionId === session.id}
                    whileTap={tapBtn}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </MotionButton>
                </MotionCard>
              ))}
            </div>

            {/* Cancel */}
            <div className="ld10-dlg-foot">
              <Button type="button" variant="outline" className="ld10-cancel" onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign10;
